// src/components/HomeSwitch.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const STORAGE_KEY = "home_default_route"; // "smart-alert" (ON) | "dashboard" (OFF)

/**
 * HomeSwitch
 * - ON  => sets localStorage.home_default_route = "smart-alert" and redirects to /smart-alert
 * - OFF => sets localStorage.home_default_route = "dashboard"   and redirects to /dashboard
 *
 * Props:
 * - onToggle?: ({ isHome: boolean, route: "smart-alert" | "dashboard" }) => void
 */
const HomeSwitch = ({ onToggle }) => {
  const [isHome, setIsHome] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Load persisted state
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "smart-alert") setIsHome(true);
    } catch {
      // ignore storage errors
    }
  }, []);

  const handleToggle = () => {
    const next = !isHome;
    setIsHome(next);

    const route = next ? "smart-alert" : "dashboard";
    try {
      localStorage.setItem(STORAGE_KEY, route);
    } catch {
      // ignore storage errors
    }

    // Notify parent (optional)
    if (onToggle) onToggle({ isHome: next, route });

    // Immediate redirect so behavior is deterministic
    const targetPath = route === "smart-alert" ? "/smart-alert" : "/dashboard";
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  };

  return (
    <label style={styles.container} onClick={handleToggle}>
      <span style={styles.label}>Home</span>
      <div
        style={{
          ...styles.switch,
          // Distinct palette from the other two switches:
          // ON  = brand navy, OFF = neutral gray
          backgroundColor: isHome ? "#1E2A78" : "#9CA3AF",
        }}
      >
        <div
          style={{
            ...styles.ball,
            left: isHome ? "21px" : "1px",
          }}
        />
      </div>
    </label>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    userSelect: "none",
    margin: 0,
    padding: 0,
  },
  label: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#374151",
    margin: 0,
    padding: 0,
  },
  switch: {
    width: "42px",
    height: "22px",
    borderRadius: "22px",
    position: "relative",
    transition: "background-color 0.3s",
    boxSizing: "border-box",
  },
  ball: {
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    backgroundColor: "#fff",
    position: "absolute",
    top: "2px",
    transition: "left 0.25s ease-in-out",
    left: "1px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.4)",
  },
};

export default HomeSwitch;
